import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Stage,
  Layer,
  Image as KonvaImage,
  Text,
  Transformer,
  Rect,
  Line,
  Circle,
  Arc,
  Group,
} from "react-konva";
import Sidebar from "../../../components/Sidebar";
import { getBrokerToolTemplate, saveBrokerEditingTemplate } from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import {
  getPermissionControllers,
  hasBrokerPermission,
  isBroker,
} from "../../../utils/permissionHelper";
import PermissionBrokerModelController from "../../../controller/model/PermissionBrokerModelController";
import { useToast } from "../../../contexts/ToastContext";
import "../../css/IbDashboard.css";
import "../../css/IbBrokerTools.css";
import "../../css/IbBrokerToolsEditor.css";

const DRAFT_PREFIX = "ibBrokerToolEditorDraft_v1::";
const LIBRARY_PREFIX = "ibBrokerToolImageLibrary_v1::";
const MAX_LIBRARY_IMAGES = 16;
const BG_LAYER_ID = "__background__";

/** Color palette matching the editor mockup. */
const TEXT_FILL_PRESETS = [
  "#fbbf24",
  "#14b8a6",
  "#3b82f6",
  "#ef4444",
  "#ec4899",
  "#a855f7",
  "#d946ef",
  "#22d3ee",
  "#ffffff",
  "#000000",
];

const FONT_PRESETS = [
  { id: "satoshi", label: "Satoshi", family: "DM Sans, system-ui, sans-serif", sample: "Aa", kind: "Sans Serif" },
  { id: "clash", label: "Clash Display", family: "Archivo Black, Impact, sans-serif", sample: "Aa", kind: "Display" },
  { id: "playfair", label: "Playfair", family: "Playfair Display, Georgia, serif", sample: "Aa", kind: "Serif" },
  { id: "space", label: "Space Mono", family: "Space Mono, ui-monospace, monospace", sample: "Aa", kind: "Monospace" },
];

const ELEMENT_PRESETS = [
  { id: "bars", icon: "fa-chart-column", label: "Bar chart" },
  { id: "shapes", icon: "fa-shapes", label: "Shapes" },
  { id: "grid", icon: "fa-table-cells", label: "Grid" },
  { id: "pie", icon: "fa-chart-pie", label: "Pie chart" },
  { id: "list", icon: "fa-list-ul", label: "List" },
  { id: "nodes", icon: "fa-share-nodes", label: "Network" },
];

const normalizeHex6 = (v) => {
  if (typeof v !== "string") return "#0f172a";
  let s = v.trim();
  if (!s) return "#0f172a";
  if (!s.startsWith("#")) s = `#${s}`;
  if (/^#[0-9A-Fa-f]{6}$/i.test(s)) return s.slice(0, 7).toLowerCase();
  if (/^#[0-9A-Fa-f]{3}$/i.test(s)) {
    const r = s[1];
    const g = s[2];
    const b = s[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return "#0f172a";
};

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const userStorageKey = (user) => {
  if (!user || typeof user !== "object") return "guest";
  const k =
    user.USER_CODE ??
    user.user_code ??
    user.USER_ID ??
    user.user_id ??
    user.ID ??
    user.id ??
    user.EMAIL ??
    user.email;
  return String(k || "guest");
};

const inferMediaFromBlob = (blob) => {
  const ct = String(blob?.type || "").toLowerCase();
  if (ct.startsWith("video/")) return "video";
  if (ct.startsWith("image/")) return "image";
  return "unknown";
};

async function fileToJpegDataUrl(file, maxSide = 1600, quality = 0.88) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const u = URL.createObjectURL(file);
    img.onload = () => {
      try {
        URL.revokeObjectURL(u);
        let w = img.naturalWidth;
        let h = img.naturalHeight;
        if (w < 1 || h < 1) {
          resolve(null);
          return;
        }
        if (w > maxSide || h > maxSide) {
          const r = Math.min(maxSide / w, maxSide / h);
          w = Math.round(w * r);
          h = Math.round(h * r);
        }
        const c = document.createElement("canvas");
        c.width = w;
        c.height = h;
        const ctx = c.getContext("2d");
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL("image/jpeg", quality));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => {
      try {
        URL.revokeObjectURL(u);
      } catch (_) {}
      reject(new Error("Image load failed"));
    };
    img.src = u;
  });
}

function loadHtmlImage(src) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = src;
  });
}

function layerLabel(item, t) {
  if (!item) return "";
  if (item.type === "image") {
    return t("ib_broker_tools_editor_layer_image", { defaultValue: "Image layer" });
  }
  if (item.type === "shape") {
    return item.label || t("ib_broker_tools_editor_layer_shape", { defaultValue: "Shape" });
  }
  const raw = String(item.text || "").replace(/\s+/g, " ").trim();
  if (!raw) {
    return item.type === "description"
      ? t("ib_broker_tools_editor_add_desc", { defaultValue: "Description" })
      : t("ib_broker_tools_editor_add_title", { defaultValue: "Title" });
  }
  return raw.length > 36 ? `${raw.slice(0, 36)}…` : raw;
}

function BrokerToolKonvaImage({
  item,
  onSelect,
  onDragEnd,
  onTransformEnd,
}) {
  const nodeRef = useRef(null);
  const [kimg, setKimg] = useState(null);

  useEffect(() => {
    let alive = true;
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (alive) setKimg(img);
    };
    img.onerror = () => {
      if (alive) setKimg(null);
    };
    img.src = item.imageSrc;
    return () => {
      alive = false;
    };
  }, [item.imageSrc]);

  // Hit-test the visible image pixels (not the empty rectangular box).
  useEffect(() => {
    const node = nodeRef.current;
    if (!node || !kimg) return;
    try {
      node.clearCache();
      node.cache();
      node.drawHitFromCache();
      node.getLayer()?.batchDraw();
    } catch (_) {
      /* cache can fail for some sources — keep default hit box */
    }
  }, [kimg, item.width, item.height, item.imageSrc, item.rotation]);

  if (!kimg) return null;

  const selectSelf = (e) => {
    e.cancelBubble = true;
    onSelect(item.id);
  };

  return (
    <KonvaImage
      ref={nodeRef}
      id={item.id}
      image={kimg}
      x={item.x}
      y={item.y}
      width={item.width}
      height={item.height}
      rotation={item.rotation || 0}
      draggable
      onMouseDown={selectSelf}
      onTouchStart={selectSelf}
      onClick={selectSelf}
      onTap={selectSelf}
      onDragEnd={onDragEnd}
      onTransformEnd={onTransformEnd}
    />
  );
}

export default function BrokerToolEditor() {
  const { t } = useTranslation("mobile");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { error: showError, success: showSuccess } = useToast();

  const templateName = useMemo(() => {
    const raw = searchParams.get("template") || "";
    return String(raw).trim();
  }, [searchParams]);
  const toolTitle = useMemo(() => {
    const raw = searchParams.get("title") || "";
    return String(raw).trim();
  }, [searchParams]);

  const brokerToolId = useMemo(() => {
    const raw = searchParams.get("toolId") || searchParams.get("brokerToolId") || "";
    const n = parseInt(String(raw).trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);

  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(true);
  const [bgImage, setBgImage] = useState(null);
  const [stageSize, setStageSize] = useState({ width: 1080, height: 1080 });
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [editingTextId, setEditingTextId] = useState(null);
  const [imageLibrary, setImageLibrary] = useState([]);
  const [exporting, setExporting] = useState(false);

  const stageRef = useRef(null);
  const transformerRef = useRef(null);
  const fileInputRef = useRef(null);
  const bgUrlRef = useRef(null);
  const inlineTextRef = useRef(null);

  const permissions = useMemo(() => getPermissionControllers(user), [user]);
  const { brokerPerm, brokerDevicePerm } = permissions;

  const canViewBrokerTools = useMemo(() => {
    if (!isBroker(user)) return false;
    return hasBrokerPermission(
      brokerPerm,
      brokerDevicePerm,
      PermissionBrokerModelController.PER__CAN_VIEW_BROKER_TOOLS
    );
  }, [user, brokerPerm, brokerDevicePerm]);

  const draftKey = useMemo(() => {
    if (!templateName) return null;
    return `${DRAFT_PREFIX}${userStorageKey(user)}::${templateName}`;
  }, [user, templateName]);

  const libraryKey = useMemo(() => `${LIBRARY_PREFIX}${userStorageKey(user)}`, [user]);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("userData");
      if (stored) setUser(JSON.parse(stored));
    } catch (_) {
      setUser(null);
    } finally {
      setLoadingUser(false);
    }
  }, []);

  useEffect(() => {
    if (!libraryKey) return;
    try {
      const raw = localStorage.getItem(libraryKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) setImageLibrary(parsed.slice(0, MAX_LIBRARY_IMAGES));
    } catch (_) {}
  }, [libraryKey]);

  const addToLibrary = useCallback(
    (dataUrl) => {
      if (!dataUrl || typeof dataUrl !== "string") return;
      const entry = { id: newId(), dataUrl, createdAt: Date.now() };
      setImageLibrary((prev) => {
        const withoutDup = prev.filter((p) => p.dataUrl !== dataUrl);
        const next = [entry, ...withoutDup].slice(0, MAX_LIBRARY_IMAGES);
        try {
          localStorage.setItem(libraryKey, JSON.stringify(next));
        } catch (_) {
          showError(
            t("ib_broker_tools_editor", { defaultValue: "Template editor" }),
            t("ib_broker_tools_library_full", { defaultValue: "Could not save image library (storage full)." })
          );
        }
        return next;
      });
    },
    [libraryKey, showError, t]
  );

  const loadOriginalTemplate = useCallback(async () => {
    if (!templateName) return false;
    setLoadingTemplate(true);
    setLoadError(null);
    try {
      const res = await getBrokerToolTemplate(templateName, { original: true, cacheBust: true });
      const blob = res?.data;
      if (!blob || !(blob instanceof Blob)) {
        throw new Error("Invalid template response");
      }
      if (inferMediaFromBlob(blob) !== "image") {
        throw new Error(
          t("ib_broker_tools_editor_image_only", {
            defaultValue: "This template must be an image to open in the editor.",
          })
        );
      }
      if (bgUrlRef.current) {
        try {
          URL.revokeObjectURL(bgUrlRef.current);
        } catch (_) {}
        bgUrlRef.current = null;
      }
      const url = URL.createObjectURL(blob);
      bgUrlRef.current = url;
      const img = await loadHtmlImage(url);
      const w = img.naturalWidth || img.width || 1080;
      const h = img.naturalHeight || img.height || 1080;
      setStageSize({ width: w, height: h });
      setBgImage(img);
      if (draftKey) {
        try {
          localStorage.removeItem(draftKey);
        } catch (_) {}
      }
      setItems([]);
      setSelectedId(null);
      setEditingTextId(null);
      return true;
    } catch (err) {
      setLoadError(err?.message || "Failed to load template.");
      setBgImage(null);
      return false;
    } finally {
      setLoadingTemplate(false);
    }
  }, [templateName, draftKey, t]);

  useEffect(() => {
    if (loadingUser) return;
    if (!canViewBrokerTools || !templateName) return;

    let cancelled = false;
    (async () => {
      await loadOriginalTemplate();
      if (cancelled) return;
    })();

    return () => {
      cancelled = true;
    };
  }, [loadingUser, canViewBrokerTools, templateName, loadOriginalTemplate]);

  useEffect(() => {
    return () => {
      if (bgUrlRef.current) {
        try {
          URL.revokeObjectURL(bgUrlRef.current);
        } catch (_) {}
        bgUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const tr = transformerRef.current;
    const stage = stageRef.current;
    if (!tr || !stage) return;
    if (!selectedId || editingTextId || selectedId === BG_LAYER_ID) {
      tr.nodes([]);
      tr.getLayer()?.batchDraw();
      return;
    }
    const node = stage.findOne((n) => n.id() === selectedId);
    if (node && node.visible()) {
      tr.nodes([node]);
    } else {
      tr.nodes([]);
    }
    tr.getLayer()?.batchDraw();
  }, [selectedId, editingTextId, items, bgImage, loadingTemplate]);

  const displayScale = useMemo(() => {
    const maxW =
      typeof window !== "undefined"
        ? Math.min(720, Math.max(240, window.innerWidth - 640))
        : 520;
    const s = Math.min(maxW / stageSize.width, 1);
    return Number.isFinite(s) && s > 0 ? s : 1;
  }, [stageSize.width]);

  const updateItem = useCallback((id, patch) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const finishTextEdit = useCallback(() => {
    setEditingTextId(null);
  }, []);

  const startTextEdit = useCallback((id) => {
    if (!id || id === BG_LAYER_ID) return;
    setSelectedId(id);
    setEditingTextId(id);
  }, []);

  useEffect(() => {
    if (!editingTextId) return;
    const el = inlineTextRef.current;
    if (!el) return;
    el.focus();
    const len = el.value?.length ?? 0;
    try {
      el.setSelectionRange(len, len);
    } catch (_) {
      /* ignore */
    }
  }, [editingTextId]);

  // Type-to-edit when a text layer is selected (no need for the sidebar box).
  useEffect(() => {
    const onKey = (e) => {
      if (editingTextId) return;
      if (!selectedId) return;
      const item = items.find((i) => i.id === selectedId);
      if (!item || (item.type !== "text" && item.type !== "description")) return;
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key === "Enter" || e.key === "F2") {
        e.preventDefault();
        startTextEdit(selectedId);
        return;
      }
      // Printable character → start editing and replace/append
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        updateItem(selectedId, { text: e.key });
        startTextEdit(selectedId);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editingTextId, selectedId, items, startTextEdit, updateItem]);

  const handleDragEnd = useCallback(
    (id) => (e) => {
      const n = e.target;
      updateItem(id, { x: n.x(), y: n.y() });
    },
    [updateItem]
  );

  const handleImageTransformEnd = useCallback(
    (id) => (e) => {
      const node = e.target;
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();
      node.scaleX(1);
      node.scaleY(1);
      const width = Math.max(32, node.width() * scaleX);
      const height = Math.max(32, node.height() * scaleY);
      // Apply size on the node immediately so hit-cache / transformer stay on the image.
      if (typeof node.width === "function") node.width(width);
      if (typeof node.height === "function") node.height(height);
      try {
        if (typeof node.clearCache === "function") {
          node.clearCache();
          node.cache?.();
          node.drawHitFromCache?.();
        }
      } catch (_) {
        /* ignore */
      }
      updateItem(id, { x: node.x(), y: node.y(), width, height, rotation: node.rotation() });
    },
    [updateItem]
  );

  const handleTextTransformEnd = useCallback(
    (id) => (e) => {
      const node = e.target;
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();
      node.scaleX(1);
      node.scaleY(1);
      const width = Math.max(40, node.width() * scaleX);
      const fs = Math.max(10, (node.fontSize() || 24) * scaleY);
      updateItem(id, {
        x: node.x(),
        y: node.y(),
        width,
        fontSize: fs,
        rotation: node.rotation(),
      });
    },
    [updateItem]
  );

  const addTextLayer = useCallback(
    (kind) => {
      const isDesc = kind === "description";
      const id = newId();
      const fontSize = isDesc ? 22 : 34;
      const text = isDesc
        ? t("ib_broker_tools_editor_desc_placeholder", { defaultValue: "Description text" })
        : t("ib_broker_tools_editor_title_placeholder", { defaultValue: "Title text" });
      setItems((prev) => [
        ...prev,
        {
          id,
          type: isDesc ? "description" : "text",
          x: stageSize.width * 0.08,
          y: stageSize.height * (isDesc ? 0.55 : 0.12),
          width: stageSize.width * 0.84,
          height: fontSize * 4,
          rotation: 0,
          text,
          fontSize,
          fill: "#ffffff",
          fontFamily: FONT_PRESETS[0].family,
          visible: true,
        },
      ]);
      setSelectedId(id);
      setEditingTextId(id);
    },
    [stageSize.width, stageSize.height, t]
  );

  const toggleLayerVisible = useCallback((id) => {
    if (id === BG_LAYER_ID) return;
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, visible: it.visible === false } : it))
    );
  }, []);

  const addElementLayer = useCallback(
    (elementId) => {
      const id = newId();
      const cx = stageSize.width * 0.55;
      const cy = stageSize.height * 0.42;
      let item = null;

      if (elementId === "bars") {
        item = {
          id,
          type: "shape",
          shape: "bars",
          label: t("ib_broker_tools_editor_el_bars", { defaultValue: "Bar chart" }),
          x: cx - 90,
          y: cy - 40,
          width: 180,
          height: 90,
          rotation: 0,
          fill: "#fbbf24",
          visible: true,
        };
      } else if (elementId === "shapes") {
        item = {
          id,
          type: "shape",
          shape: "rect",
          label: t("ib_broker_tools_editor_el_shapes", { defaultValue: "Accent shape" }),
          x: cx - 60,
          y: cy - 60,
          width: 120,
          height: 120,
          rotation: 0,
          fill: "#fbbf24",
          cornerRadius: 18,
          opacity: 0.85,
          visible: true,
        };
      } else if (elementId === "grid") {
        item = {
          id,
          type: "shape",
          shape: "grid",
          label: t("ib_broker_tools_editor_el_grid", { defaultValue: "Layout grid" }),
          x: cx - 100,
          y: cy - 70,
          width: 200,
          height: 140,
          rotation: 0,
          fill: "#ffffff",
          visible: true,
        };
      } else if (elementId === "pie") {
        item = {
          id,
          type: "shape",
          shape: "pie",
          label: t("ib_broker_tools_editor_el_pie", { defaultValue: "Pie chart" }),
          x: cx,
          y: cy,
          width: 100,
          height: 100,
          rotation: 0,
          fill: "#fbbf24",
          visible: true,
        };
      } else if (elementId === "list") {
        item = {
          id,
          type: "description",
          x: stageSize.width * 0.08,
          y: stageSize.height * 0.62,
          width: stageSize.width * 0.7,
          height: 120,
          rotation: 0,
          text: "• Feature highlight one\n• Feature highlight two\n• Feature highlight three",
          fontSize: 20,
          fill: "#ffffff",
          fontFamily: FONT_PRESETS[0].family,
          visible: true,
        };
        setItems((prev) => [...prev, item]);
        setSelectedId(id);
        setEditingTextId(id);
        return;
      } else if (elementId === "nodes") {
        item = {
          id,
          type: "shape",
          shape: "nodes",
          label: t("ib_broker_tools_editor_el_nodes", { defaultValue: "Network nodes" }),
          x: cx - 80,
          y: cy - 40,
          width: 160,
          height: 80,
          rotation: 0,
          fill: "#22d3ee",
          visible: true,
        };
      }

      if (!item) return;
      setItems((prev) => [...prev, item]);
      setSelectedId(id);
    },
    [stageSize.width, stageSize.height, t]
  );

  const addImageFromDataUrl = useCallback(
    (dataUrl) => {
      if (!dataUrl) return;
      const img = new window.Image();
      img.onload = () => {
        const maxW = stageSize.width * 0.45;
        const maxH = stageSize.height * 0.45;
        let w = img.naturalWidth || img.width;
        let h = img.naturalHeight || img.height;
        if (w < 1) w = 200;
        if (h < 1) h = 200;
        const r = Math.min(maxW / w, maxH / h, 1);
        w = Math.round(w * r);
        h = Math.round(h * r);
        const id = newId();
        setItems((prev) => [
          ...prev,
          {
            id,
            type: "image",
            x: stageSize.width * 0.25,
            y: stageSize.height * 0.25,
            width: w,
            height: h,
            rotation: 0,
            imageSrc: dataUrl,
            visible: true,
          },
        ]);
        setSelectedId(id);
      };
      img.src = dataUrl;
    },
    [stageSize.width, stageSize.height]
  );

  const onPickFile = useCallback(
    async (e) => {
      const file = e.target?.files?.[0];
      e.target.value = "";
      if (!file || !file.type.startsWith("image/")) {
        showError(
          t("ib_broker_tools_editor", { defaultValue: "Template editor" }),
          t("ib_broker_tools_editor_need_image", { defaultValue: "Please choose an image file." })
        );
        return;
      }
      try {
        const dataUrl = await fileToJpegDataUrl(file, 2000, 0.9);
        if (!dataUrl) throw new Error("Could not read image");
        addToLibrary(dataUrl);
        addImageFromDataUrl(dataUrl);
      } catch (err) {
        showError(
          t("ib_broker_tools_editor", { defaultValue: "Template editor" }),
          err?.message || "Failed to add image."
        );
      }
    },
    [addImageFromDataUrl, addToLibrary, showError, t]
  );

  const deleteSelected = useCallback(() => {
    if (!selectedId || selectedId === BG_LAYER_ID) return;
    if (editingTextId === selectedId) setEditingTextId(null);
    setItems((prev) => prev.filter((it) => it.id !== selectedId));
    setSelectedId(null);
  }, [selectedId, editingTextId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Delete" || e.key === "Backspace") {
        const tag = (e.target && e.target.tagName) || "";
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        deleteSelected();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deleteSelected]);

  const selectedItem = useMemo(() => items.find((i) => i.id === selectedId) || null, [items, selectedId]);
  const editingItem = useMemo(
    () => items.find((i) => i.id === editingTextId) || null,
    [items, editingTextId]
  );

  const handleDownloadPng = useCallback(async () => {
    const stage = stageRef.current;
    const tr = transformerRef.current;
    if (!stage) return;

    try {
      setExporting(true);
      if (tr) {
        tr.nodes([]);
        tr.getLayer()?.batchDraw();
      }
      stage.batchDraw();
      const dataUrl = stage.toDataURL({
        pixelRatio: 1,
        mimeType: "image/png",
        quality: 1,
      });

      let savedToAdmin = false;
      if (brokerToolId != null) {
        try {
          const blob = await (await fetch(dataUrl)).blob();
          const res = await saveBrokerEditingTemplate({
            brokerToolId,
            imageBlob: blob,
          });
          savedToAdmin = Number(res?.data?.CODE) === AppConstants.responseOk;
          if (!savedToAdmin) {
            showError(
              t("ib_broker_tools_editor", { defaultValue: "Template editor" }),
              t("ib_broker_tools_editor_save_failed", {
                defaultValue:
                  "PNG downloaded, but saving to IB edited templates failed. Check your profile phone/name or try again.",
              })
            );
          }
        } catch (_) {
          showError(
            t("ib_broker_tools_editor", { defaultValue: "Template editor" }),
            t("ib_broker_tools_editor_save_failed", {
              defaultValue:
                "PNG downloaded, but saving to IB edited templates failed. Check your profile phone/name or try again.",
            })
          );
        }
      }

      const a = document.createElement("a");
      const safe = templateName.replace(/[^\w-]+/g, "_").slice(0, 80) || "template";
      a.download = `broker-tool-${safe}.png`;
      a.href = dataUrl;
      a.click();
      if (brokerToolId == null || savedToAdmin) {
        showSuccess(
          t("ib_broker_tools_editor_downloaded", { defaultValue: "Image downloaded." }),
          ""
        );
      }
    } catch (err) {
      showError(
        t("ib_broker_tools_editor", { defaultValue: "Template editor" }),
        err?.message || "Export failed."
      );
    } finally {
      setExporting(false);
      if (selectedId && transformerRef.current && stageRef.current) {
        const found = stageRef.current.findOne((n) => n.id() === selectedId);
        if (found) transformerRef.current.nodes([found]);
        transformerRef.current.getLayer()?.batchDraw();
      }
    }
  }, [brokerToolId, selectedId, showError, showSuccess, t, templateName]);

  const clearDraft = useCallback(async () => {
    await loadOriginalTemplate();
    showSuccess(
      t("ib_broker_tools_editor_draft_cleared", { defaultValue: "Template reset to original." }),
      ""
    );
  }, [loadOriginalTemplate, showSuccess, t]);

  const pageHeading = toolTitle || templateName || t("ib_broker_tools_editor", { defaultValue: "Template editor" });

  if (!loadingUser && !canViewBrokerTools) {
    return (
      <div className="ib-dashboard-page ib-broker-tools-page">
        <Sidebar user={user} />
        <div className="ib-dashboard-main">
          <div className="container-fluid px-0 w-100 ib-dashboard-main-inner">
            <div className="ib-broker-tools-denied" role="alert">
              {t("ib_broker_tools_no_permission", {
                defaultValue: "You do not have permission to view broker tools.",
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!templateName) {
    return (
      <div className="ib-dashboard-page ib-broker-tools-page">
        <Sidebar user={user} />
        <div className="ib-dashboard-main">
          <div className="container-fluid px-0 w-100 ib-dashboard-main-inner ib-broker-tools-editor-wrap">
            <p className="ib-broker-tools-editor-error">
              {t("ib_broker_tools_editor_missing_template", { defaultValue: "No template selected." })}
            </p>
            <button type="button" className="btn btn-light ib-broker-tools-editor-back" onClick={() => navigate("/pages/ibDashboard/broker-tools")}>
              {t("ib_broker_tools_editor_back", { defaultValue: "Back to broker tools" })}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ib-dashboard-page ib-broker-tools-page ib-broker-tools-editor-page">
      <Sidebar user={user} />
      <div className="ib-dashboard-main">
        <div className="container-fluid px-0 w-100 ib-dashboard-main-inner ib-broker-tools-editor-wrap">
          <header className="ib-broker-tools-editor-head">
            <h1 className="ib-broker-tools-editor-title">{pageHeading}</h1>
            <p className="ib-broker-tools-editor-sub">
              {t("ib_broker_tools_editor_sub", {
                defaultValue:
                  "Customize your broker template with layers, typography, colors, and elements — then export a PNG.",
              })}
            </p>
          </header>

          {loadError && (
            <div className="ib-broker-tools-editor-error" role="alert">
              {loadError}
              <div className="mt-2">
                <button type="button" className="btn btn-light btn-sm" onClick={() => navigate("/pages/ibDashboard/broker-tools")}>
                  {t("ib_broker_tools_editor_back", { defaultValue: "Back" })}
                </button>
              </div>
            </div>
          )}

          {!loadError && (
            <div className="ib-broker-tools-editor-layout">
              <aside className="ib-broker-tools-editor-sidebar ib-broker-tools-editor-sidebar--left">
                <div className="ib-broker-tools-editor-panel ib-broker-tools-editor-tools">
                  <span className="ib-broker-tools-editor-tools-label">
                    {t("ib_broker_tools_editor_add", { defaultValue: "Add" })}
                  </span>
                  <div className="ib-broker-tools-editor-btn-stack">
                    <button
                      type="button"
                      className="ib-broker-tools-editor-btn ib-broker-tools-editor-btn--ghost"
                      onClick={() => addTextLayer("text")}
                    >
                      <i className="fa-solid fa-align-left" aria-hidden />
                      {t("ib_broker_tools_editor_add_title", { defaultValue: "Title" })}
                    </button>
                    <button
                      type="button"
                      className="ib-broker-tools-editor-btn ib-broker-tools-editor-btn--ghost"
                      onClick={() => addTextLayer("description")}
                    >
                      <i className="fa-solid fa-paragraph" aria-hidden />
                      {t("ib_broker_tools_editor_add_desc", { defaultValue: "Description" })}
                    </button>
                    <button
                      type="button"
                      className="ib-broker-tools-editor-btn ib-broker-tools-editor-btn--ghost"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <i className="fa-regular fa-image" aria-hidden />
                      {t("ib_broker_tools_editor_add_image", { defaultValue: "Image" })}
                    </button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    tabIndex={-1}
                    aria-hidden="true"
                    className="ib-broker-tools-editor-file-input-hidden"
                    onChange={onPickFile}
                  />
                </div>

                <div className="ib-broker-tools-editor-panel ib-broker-tools-editor-layers">
                  <span className="ib-broker-tools-editor-tools-label">
                    {t("ib_broker_tools_editor_layers", { defaultValue: "Layers" })}
                  </span>
                  <ul className="ib-broker-tools-editor-layer-list">
                    {[...items].reverse().map((it) => {
                      const active = selectedId === it.id;
                      const isVisible = it.visible !== false;
                      return (
                        <li key={it.id}>
                          <button
                            type="button"
                            className={`ib-broker-tools-editor-layer-item${
                              active ? " ib-broker-tools-editor-layer-item--active" : ""
                            }`}
                            onClick={() => setSelectedId(it.id)}
                          >
                            <span className="ib-broker-tools-editor-layer-name">
                              {layerLabel(it, t)}
                            </span>
                            <span
                              className="ib-broker-tools-editor-layer-eye"
                              role="button"
                              tabIndex={0}
                              aria-label={
                                isVisible
                                  ? t("ib_broker_tools_editor_hide_layer", { defaultValue: "Hide layer" })
                                  : t("ib_broker_tools_editor_show_layer", { defaultValue: "Show layer" })
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleLayerVisible(it.id);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleLayerVisible(it.id);
                                }
                              }}
                            >
                              <i className={`fa-regular ${isVisible ? "fa-eye" : "fa-eye-slash"}`} aria-hidden />
                            </span>
                          </button>
                        </li>
                      );
                    })}
                    <li>
                      <button
                        type="button"
                        className={`ib-broker-tools-editor-layer-item${
                          selectedId === BG_LAYER_ID ? " ib-broker-tools-editor-layer-item--active" : ""
                        }`}
                        onClick={() => setSelectedId(BG_LAYER_ID)}
                      >
                        <span className="ib-broker-tools-editor-layer-name">
                          {t("ib_broker_tools_editor_layer_bg", {
                            defaultValue: "Background template",
                          })}
                        </span>
                        <span className="ib-broker-tools-editor-layer-eye" aria-hidden>
                          <i className="fa-regular fa-eye" />
                        </span>
                      </button>
                    </li>
                  </ul>
                </div>

                {imageLibrary.length > 0 && (
                  <div className="ib-broker-tools-editor-panel ib-broker-tools-editor-library">
                    <div className="ib-broker-tools-editor-library-head">
                      {t("ib_broker_tools_editor_library", { defaultValue: "My images" })}
                    </div>
                    <div className="ib-broker-tools-editor-library-grid">
                      {imageLibrary.map((entry) => (
                        <button
                          key={entry.id}
                          type="button"
                          className="ib-broker-tools-editor-library-thumb"
                          title={t("ib_broker_tools_editor_add_to_canvas", { defaultValue: "Add to canvas" })}
                          onClick={() => addImageFromDataUrl(entry.dataUrl)}
                        >
                          <img src={entry.dataUrl} alt="" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="ib-broker-tools-editor-panel ib-broker-tools-editor-footer-actions">
                  <button
                    type="button"
                    className="ib-broker-tools-editor-btn ib-broker-tools-editor-btn--muted ib-broker-tools-editor-btn--block"
                    onClick={clearDraft}
                  >
                    <i className="fa-solid fa-rotate-left" aria-hidden />
                    {t("ib_broker_tools_editor_reset", { defaultValue: "Reset Canvas" })}
                  </button>
                  <button
                    type="button"
                    className="ib-broker-tools-editor-btn ib-broker-tools-editor-btn--gold ib-broker-tools-editor-btn--block"
                    disabled={loadingTemplate || !bgImage || exporting}
                    onClick={handleDownloadPng}
                  >
                    {exporting ? (
                      t("ib_broker_tools_editor_exporting", { defaultValue: "Working…" })
                    ) : (
                      <>
                        <i className="fa-solid fa-download" aria-hidden />
                        {t("ib_broker_tools_editor_export", {
                          defaultValue: "Export / Download PNG",
                        })}
                      </>
                    )}
                  </button>
                </div>
              </aside>

              <div className="ib-broker-tools-editor-canvas-wrap">
                {loadingTemplate && (
                  <div className="ib-broker-tools-editor-loading" aria-busy="true">
                    <span
                      className="ib-dashboard-skeleton ib-dashboard-skeleton--kpi-value"
                      style={{ width: "120px", height: "20px" }}
                    />
                  </div>
                )}
                {!loadingTemplate && bgImage && (
                  <div
                    className="ib-broker-tools-editor-stage-outer"
                    style={{
                      width: stageSize.width * displayScale,
                      height: stageSize.height * displayScale,
                    }}
                  >
                    <div
                      className="ib-broker-tools-editor-stage-scaler"
                      style={{
                        width: stageSize.width,
                        height: stageSize.height,
                        transform: `scale(${displayScale})`,
                        transformOrigin: "top left",
                      }}
                    >
                      <Stage ref={stageRef} width={stageSize.width} height={stageSize.height}>
                        <Layer listening={false}>
                          <KonvaImage
                            image={bgImage}
                            x={0}
                            y={0}
                            width={stageSize.width}
                            height={stageSize.height}
                            listening={false}
                          />
                        </Layer>
                        <Layer>
                          <Rect
                            x={0}
                            y={0}
                            width={stageSize.width}
                            height={stageSize.height}
                            fill="transparent"
                            onMouseDown={() => {
                              if (editingTextId) finishTextEdit();
                              setSelectedId(null);
                            }}
                            onTouchStart={() => {
                              if (editingTextId) finishTextEdit();
                              setSelectedId(null);
                            }}
                          />
                          {items.map((it) => {
                            if (it.visible === false) return null;

                            if (it.type === "image") {
                              return (
                                <BrokerToolKonvaImage
                                  key={it.id}
                                  item={it}
                                  onSelect={(id) => {
                                    if (editingTextId) finishTextEdit();
                                    setSelectedId(id);
                                  }}
                                  onDragEnd={handleDragEnd(it.id)}
                                  onTransformEnd={handleImageTransformEnd(it.id)}
                                />
                              );
                            }

                            if (it.type === "text" || it.type === "description") {
                              const isEditing = editingTextId === it.id;
                              return (
                                <Text
                                  key={it.id}
                                  id={it.id}
                                  text={it.text || ""}
                                  x={it.x}
                                  y={it.y}
                                  width={it.width}
                                  fontSize={it.fontSize}
                                  fontFamily={it.fontFamily || FONT_PRESETS[0].family}
                                  fill={normalizeHex6(it.fill)}
                                  rotation={it.rotation || 0}
                                  draggable={!isEditing}
                                  visible={!isEditing}
                                  wrap="word"
                                  onMouseDown={(e) => {
                                    e.cancelBubble = true;
                                    setSelectedId(it.id);
                                  }}
                                  onTouchStart={(e) => {
                                    e.cancelBubble = true;
                                    setSelectedId(it.id);
                                  }}
                                  onClick={() => setSelectedId(it.id)}
                                  onTap={() => setSelectedId(it.id)}
                                  onDblClick={() => startTextEdit(it.id)}
                                  onDblTap={() => startTextEdit(it.id)}
                                  onDragEnd={handleDragEnd(it.id)}
                                  onTransformEnd={handleTextTransformEnd(it.id)}
                                />
                              );
                            }

                            if (it.type === "shape") {
                              const fill = normalizeHex6(it.fill || "#fbbf24");
                              const groupProps = {
                                id: it.id,
                                x: it.x,
                                y: it.y,
                                width: it.width,
                                height: it.height,
                                rotation: it.rotation || 0,
                                draggable: true,
                                onMouseDown: (e) => {
                                  e.cancelBubble = true;
                                },
                                onTouchStart: (e) => {
                                  e.cancelBubble = true;
                                },
                                onClick: () => setSelectedId(it.id),
                                onTap: () => setSelectedId(it.id),
                                onDragEnd: handleDragEnd(it.id),
                                onTransformEnd: handleImageTransformEnd(it.id),
                              };

                              if (it.shape === "bars") {
                                const bw = Math.max(8, it.width / 5);
                                const gaps = 8;
                                return (
                                  <Group key={it.id} {...groupProps}>
                                    {[0.45, 0.7, 0.55, 0.9, 0.65].map((hRatio, idx) => {
                                      const barH = it.height * hRatio;
                                      return (
                                        <Rect
                                          key={`bar-${idx}`}
                                          x={idx * (bw + gaps)}
                                          y={it.height - barH}
                                          width={bw}
                                          height={barH}
                                          fill={fill}
                                          cornerRadius={4}
                                          opacity={0.9}
                                        />
                                      );
                                    })}
                                  </Group>
                                );
                              }

                              if (it.shape === "grid") {
                                const cols = 3;
                                const rows = 2;
                                const cellW = it.width / cols;
                                const cellH = it.height / rows;
                                const cells = [];
                                for (let r = 0; r < rows; r += 1) {
                                  for (let c = 0; c < cols; c += 1) {
                                    cells.push(
                                      <Rect
                                        key={`g-${r}-${c}`}
                                        x={c * cellW + 4}
                                        y={r * cellH + 4}
                                        width={cellW - 8}
                                        height={cellH - 8}
                                        stroke={fill}
                                        strokeWidth={2}
                                        cornerRadius={6}
                                        opacity={0.55}
                                      />
                                    );
                                  }
                                }
                                return (
                                  <Group key={it.id} {...groupProps}>
                                    <Rect
                                      width={it.width}
                                      height={it.height}
                                      fill="rgba(0,0,0,0.01)"
                                    />
                                    {cells}
                                  </Group>
                                );
                              }

                              if (it.shape === "pie") {
                                const r = Math.max(20, Math.min(it.width, it.height) / 2);
                                return (
                                  <Group key={it.id} {...groupProps}>
                                    <Circle x={r} y={r} radius={r} fill="#1f2937" opacity={0.9} />
                                    <Arc
                                      x={r}
                                      y={r}
                                      innerRadius={0}
                                      outerRadius={r}
                                      angle={230}
                                      rotation={-90}
                                      fill={fill}
                                    />
                                  </Group>
                                );
                              }

                              if (it.shape === "nodes") {
                                return (
                                  <Group key={it.id} {...groupProps}>
                                    <Line
                                      points={[0, 20, it.width, it.height - 20]}
                                      stroke={fill}
                                      strokeWidth={3}
                                      opacity={0.7}
                                    />
                                    <Circle x={0} y={20} radius={12} fill={fill} />
                                    <Circle
                                      x={it.width * 0.5}
                                      y={it.height * 0.5}
                                      radius={10}
                                      fill="#ffffff"
                                      opacity={0.85}
                                    />
                                    <Circle
                                      x={it.width}
                                      y={it.height - 20}
                                      radius={12}
                                      fill={fill}
                                    />
                                  </Group>
                                );
                              }

                              return (
                                <Rect
                                  key={it.id}
                                  id={it.id}
                                  x={it.x}
                                  y={it.y}
                                  width={it.width}
                                  height={it.height}
                                  rotation={it.rotation || 0}
                                  fill={fill}
                                  cornerRadius={it.cornerRadius || 12}
                                  opacity={it.opacity != null ? it.opacity : 0.9}
                                  draggable
                                  onMouseDown={(e) => {
                                    e.cancelBubble = true;
                                  }}
                                  onTouchStart={(e) => {
                                    e.cancelBubble = true;
                                  }}
                                  onClick={() => setSelectedId(it.id)}
                                  onTap={() => setSelectedId(it.id)}
                                  onDragEnd={handleDragEnd(it.id)}
                                  onTransformEnd={handleImageTransformEnd(it.id)}
                                />
                              );
                            }

                            return null;
                          })}
                          <Transformer
                            ref={transformerRef}
                            rotateEnabled
                            padding={0}
                            ignoreStroke
                            shouldOverdrawWholeArea={false}
                            keepRatio={selectedItem?.type === "image"}
                            borderStroke="#fbbf24"
                            anchorStroke="#fbbf24"
                            anchorFill="#0f172a"
                            boundBoxFunc={(oldBox, newBox) => {
                              if (newBox.width < 24 || newBox.height < 24) return oldBox;
                              return newBox;
                            }}
                          />
                        </Layer>
                      </Stage>
                    </div>

                    {editingItem &&
                      (editingItem.type === "text" || editingItem.type === "description") && (
                        <textarea
                          ref={inlineTextRef}
                          className="ib-broker-tools-editor-inline-text"
                          value={editingItem.text || ""}
                          aria-label={t("ib_broker_tools_editor_text", { defaultValue: "Text" })}
                          style={{
                            left: editingItem.x * displayScale,
                            top: editingItem.y * displayScale,
                            width: Math.max(48, (editingItem.width || 200) * displayScale),
                            minHeight: Math.max(
                              28,
                              (editingItem.fontSize || 24) * 1.35 * displayScale
                            ),
                            fontSize: Math.max(10, (editingItem.fontSize || 24) * displayScale),
                            fontFamily: editingItem.fontFamily || FONT_PRESETS[0].family,
                            color: normalizeHex6(editingItem.fill),
                            transform: `rotate(${editingItem.rotation || 0}deg)`,
                          }}
                          onChange={(e) =>
                            updateItem(editingItem.id, { text: e.target.value })
                          }
                          onMouseDown={(e) => e.stopPropagation()}
                          onBlur={() => finishTextEdit()}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === "Escape") {
                              e.preventDefault();
                              finishTextEdit();
                              return;
                            }
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              finishTextEdit();
                            }
                          }}
                        />
                      )}
                  </div>
                )}
              </div>

              <aside className="ib-broker-tools-editor-sidebar ib-broker-tools-editor-sidebar--right">
                <div className="ib-broker-tools-editor-panel">
                  <span className="ib-broker-tools-editor-tools-label">
                    {t("ib_broker_tools_editor_typography", { defaultValue: "Typography" })}
                  </span>
                  <div className="ib-broker-tools-editor-font-grid">
                    {FONT_PRESETS.map((font) => {
                      const active =
                        selectedItem &&
                        (selectedItem.type === "text" || selectedItem.type === "description") &&
                        (selectedItem.fontFamily || FONT_PRESETS[0].family) === font.family;
                      return (
                        <button
                          key={font.id}
                          type="button"
                          className={`ib-broker-tools-editor-font-card${
                            active ? " ib-broker-tools-editor-font-card--active" : ""
                          }`}
                          disabled={
                            !selectedItem ||
                            (selectedItem.type !== "text" && selectedItem.type !== "description")
                          }
                          onClick={() => {
                            if (!selectedItem) return;
                            updateItem(selectedItem.id, { fontFamily: font.family });
                          }}
                        >
                          <span className="ib-broker-tools-editor-font-sample" style={{ fontFamily: font.family }}>
                            {font.sample}
                          </span>
                          <span className="ib-broker-tools-editor-font-name">{font.label}</span>
                          <span className="ib-broker-tools-editor-font-kind">{font.kind}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="ib-broker-tools-editor-panel">
                  <span className="ib-broker-tools-editor-tools-label">
                    {t("ib_broker_tools_editor_colors", { defaultValue: "Colors" })}
                  </span>
                  <div className="ib-broker-tools-editor-color-swatches ib-broker-tools-editor-color-swatches--palette" role="list">
                    {TEXT_FILL_PRESETS.map((hex) => {
                      const canPaint =
                        selectedItem &&
                        (selectedItem.type === "text" ||
                          selectedItem.type === "description" ||
                          selectedItem.type === "shape");
                      const active =
                        canPaint && normalizeHex6(selectedItem.fill || "#ffffff") === hex;
                      return (
                        <button
                          key={hex}
                          type="button"
                          role="listitem"
                          className={`ib-broker-tools-editor-swatch${
                            active ? " ib-broker-tools-editor-swatch--active" : ""
                          }`}
                          style={{ backgroundColor: hex }}
                          title={hex}
                          aria-label={hex}
                          aria-pressed={active}
                          disabled={!canPaint}
                          onClick={() => updateItem(selectedItem.id, { fill: hex })}
                        />
                      );
                    })}
                  </div>
                </div>

                <div className="ib-broker-tools-editor-panel">
                  <span className="ib-broker-tools-editor-tools-label">
                    {t("ib_broker_tools_editor_elements", { defaultValue: "Elements" })}
                  </span>
                  <div className="ib-broker-tools-editor-elements-grid">
                    {ELEMENT_PRESETS.map((el) => (
                      <button
                        key={el.id}
                        type="button"
                        className="ib-broker-tools-editor-element-btn"
                        title={el.label}
                        aria-label={el.label}
                        onClick={() => addElementLayer(el.id)}
                      >
                        <i className={`fa-solid ${el.icon}`} aria-hidden />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="ib-broker-tools-editor-panel ib-broker-tools-editor-props">
                  <span className="ib-broker-tools-editor-tools-label">
                    {t("ib_broker_tools_editor_selection", { defaultValue: "Selection" })}
                  </span>
                  {selectedItem &&
                  (selectedItem.type === "text" || selectedItem.type === "description") ? (
                    <>
                      <label className="ib-broker-tools-editor-label" htmlFor="bt-edit-text">
                        {t("ib_broker_tools_editor_text", { defaultValue: "Text" })}
                      </label>
                      <textarea
                        id="bt-edit-text"
                        className="ib-broker-tools-editor-textarea"
                        rows={3}
                        value={selectedItem.text || ""}
                        onChange={(e) => updateItem(selectedItem.id, { text: e.target.value })}
                      />
                      <label
                        className="ib-broker-tools-editor-label ib-broker-tools-editor-label--spaced"
                        htmlFor="bt-edit-fs"
                      >
                        {t("ib_broker_tools_editor_font_size", { defaultValue: "Size" })}
                      </label>
                      <input
                        id="bt-edit-fs"
                        type="number"
                        className="ib-broker-tools-editor-input"
                        min={10}
                        max={200}
                        value={Math.round(selectedItem.fontSize || 24)}
                        onChange={(e) =>
                          updateItem(selectedItem.id, {
                            fontSize: Math.max(10, Number(e.target.value) || 10),
                          })
                        }
                      />
                    </>
                  ) : selectedItem && selectedItem.type === "image" ? (
                    <p className="ib-broker-tools-editor-hint">
                      {t("ib_broker_tools_editor_image_hint", {
                        defaultValue: "Drag corners to resize. Delete key removes the layer.",
                      })}
                    </p>
                  ) : selectedItem && selectedItem.type === "shape" ? (
                    <p className="ib-broker-tools-editor-hint">
                      {t("ib_broker_tools_editor_shape_hint", {
                        defaultValue: "Drag to move. Use Colors to change fill.",
                      })}
                    </p>
                  ) : (
                    <p className="ib-broker-tools-editor-hint">
                      {t("ib_broker_tools_editor_select_hint", {
                        defaultValue:
                          "Double-click text on the canvas to type. Drag corners to resize.",
                      })}
                    </p>
                  )}
                  <div className="ib-broker-tools-editor-actions">
                    <button
                      type="button"
                      className="ib-broker-tools-editor-btn ib-broker-tools-editor-btn--danger ib-broker-tools-editor-btn--block"
                      disabled={!selectedId || selectedId === BG_LAYER_ID}
                      onClick={deleteSelected}
                    >
                      {t("ib_broker_tools_editor_delete_layer", { defaultValue: "Remove layer" })}
                    </button>
                  </div>
                </div>
              </aside>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
